import React, { useState, useEffect } from "react";
import "../../styles/users.css";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationModal from "../deleteconfirmationmodal";
import { API_BASE_URL } from "../../Config/config";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchUsersAndStaff();
  }, []);

  // match users with staff names
  const matchUsersWithStaffNames = (usersData, staffData) => {
    // safety checks for both parameters
    const safeUsersData = Array.isArray(usersData) ? usersData : [];
    const safeStaffData = Array.isArray(staffData) ? staffData : [];

    // console.log("Users Data:", usersData);
    // console.log("Staff Data:", staffData);

    return safeUsersData.map((user) => {
      const matchingStaff = safeStaffData.find(
        (staff) => staff?.staff_id === user?.staff_id
      );
      return {
        ...user,
        staff_name: matchingStaff ? matchingStaff.staff_name : "Unknown",
      };
    });
  };

  const fetchUsersAndStaff = async () => {
    try {
      // users and staff data
      const [usersResponse, staffResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/auth/users`),
        axios.get(`${API_BASE_URL}/staff`),
      ]);

      // console.log("Users Response:", usersResponse.data);
      // console.log("Staff Response:", staffResponse.data);

      // Add safety checks for response data
      const usersData = usersResponse.data?.users || [];
      const staffData = staffResponse.data?.staff || [];

      // Ensure both are arrays
      const safeUsersData = Array.isArray(usersData) ? usersData : [];
      const safeStaffData = Array.isArray(staffData) ? staffData : [];

      // Match users with staff names
      const usersWithStaffNames = matchUsersWithStaffNames(
        safeUsersData,
        safeStaffData
      );

      setUsers(usersWithStaffNames);
      setFilteredUsers(usersWithStaffNames);
      setAvailableStaff(safeStaffData);
    } catch (error) {
      // console.error("Error fetching data:", error);
      toast.error("Error fetching data");
      setUsers([]);
      setFilteredUsers([]);
      setAvailableStaff([]);
    }
  };

  // const fetchUsers = () => {
  //   axios
  //     .get(`${API_BASE_URL}/auth/users`)
  //     .then((response) => {
  //       const usersData = response.data.users;
  //       // Match with existing staff data
  //       const usersWithStaffNames = matchUsersWithStaffNames(
  //         usersData,
  //         availableStaff
  //       );
  //       setUsers(usersWithStaffNames);
  //       setFilteredUsers(usersWithStaffNames);
  //     })
  //     .catch((error) => {
  //       console.error("Error fetching users data:", error);
  //     });
  // };

  // const fetchAvailableStaff = () => {
  //   axios
  //     .get(`${API_BASE_URL}/staff`)
  //     .then((response) => {
  //       setAvailableStaff(response.data.staff);
  //     })
  //     .catch((error) => {
  //       console.error("Error fetching available staff:", error);
  //     });
  // };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query === "") {
      setFilteredUsers(users);
    } else {
      // Add safety checks for users array and properties
      const safeUsers = Array.isArray(users) ? users : [];
      const filtered = safeUsers.filter(
        (user) =>
          user?.staff_id?.toLowerCase().includes(query.toLowerCase()) ||
          user?.staff_name?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  };

  const handleAddUser = () => {
    if (selectedStaffId) {
      setLoading(true);
      const selectedStaff = availableStaff.find(
        (staff) => staff.staff_id === selectedStaffId
      );

      const userExists = users.some(
        (user) => user.staff_id === selectedStaffId
      );

      if (userExists) {
        setLoading(false);
        toast.error("This staff member is already a user.");
        return;
      }

      if (editingIndex !== null) {
        // Editing logic
        axios
          .put(`${API_BASE_URL}/users/${selectedStaffId}`, selectedStaff)
          .then(() => {
            setLoading(false);
            const updatedUsers = [...users];
            updatedUsers[editingIndex] = {
              ...selectedStaff,
              staff_name: selectedStaff.staff_name,
            };
            setUsers(updatedUsers);
            setFilteredUsers(updatedUsers);
            resetForm();
            toast.success("User updated successfully");
          })
          .catch((error) => {
            setLoading(false);
            // console.error("Error updating user:", error);
            toast.error("Failed to update user");
          });
      } else {
        // Adding logic
        axios
          .post(`${API_BASE_URL}/auth/create-user`, selectedStaff)
          .then((response) => {
            setLoading(false);
            // Add staff_name to the new user data
            const newUser = {
              ...response.data,
              staff_name: selectedStaff.staff_name,
            };
            const newUserList = [...users, newUser];
            setUsers(newUserList);
            setFilteredUsers(newUserList);
            resetForm();
            toast.success("User added successfully");
            fetchUsersAndStaff();
          })
          .catch((error) => {
            setLoading(false);
            // console.error("Error adding user:", error);
            toast.error("Failed to add user");
          });
      }
    } else {
      toast.error("Please select a staff member");
    }
  };

  const handleDeleteUser = (index, staffId) => {
    setDeleteLoading(true);
    axios
      .delete(`${API_BASE_URL}/auth/delete-user/${staffId}`)
      .then((response) => {
        const updatedUsers = users.filter((_, i) => i !== index);
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
        toast.success("User deleted successfully");
        // console.log("Deleting User :", response);
        setShowDeleteModal(false);
        setUserToDelete(null);
        setDeleteLoading(false);
      })
      .catch((error) => {
        toast.error("There was an error deleting user");
        // console.error("Error deleting user:", error);
        setDeleteLoading(false);
      });
  };

  const handleDeleteClick = (index, user) => {
    setUserToDelete({ index, user });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete) {
      handleDeleteUser(userToDelete.index, userToDelete.user.staff_id);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setDeleteLoading(false);
  };

  // const handleEditUser = (index) => {
  //   setSelectedStaffId(filteredUsers[index].staff_id);
  //   setEditingIndex(index);
  //   setShowModal(true);
  // };

  const resetForm = () => {
    setSelectedStaffId("");
    setEditingIndex(null);
    setShowModal(false);
  };

  const handleDownloadAll = () => {
    // Add safety check for filteredUsers
    const safeFilteredUsers = Array.isArray(filteredUsers) ? filteredUsers : [];

    if (safeFilteredUsers.length === 0) {
      toast.warning("No user data to download");
      return;
    }

    const csvData = safeFilteredUsers
      .map(
        ({ username, staff_id, staff_name }) =>
          `${username || ""},${staff_id || ""},${staff_name || ""}`
      )
      .join("\n");
    const blob = new Blob([`Username,Staff ID,Staff Name\n${csvData}`], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="users-container">
      <ToastContainer />
      <h1
        style={{
          fontSize: "30px",
          marginBottom: "20px",
          fontWeight: "bold",
          color: "#000000",
        }}
      >
        Users
      </h1>
      <div className="users-header">
        <input
          type="text"
          placeholder="search users..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="search-box"
        />
        <button className="add-user-btn" onClick={() => setShowModal(true)}>
          Add User
        </button>
        <button className="download-btn" onClick={handleDownloadAll}>
          Download All
        </button>
      </div>

      {loading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Adding User...</p>
        </div>
      )}

      <table className="users-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Staff ID</th>
            <th>Staff Name</th>
            {/* <th>Phone</th>
            <th>Email</th>  */}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user, index) => (
              <tr key={index}>
                <td>{user.username}</td>
                <td>{user.staff_id}</td>
                <td>{user.staff_name || "Unknown"}</td>
                {/* <td>{user.phone}</td>
                <td>{user.email}</td>  */}
                <td>
                  {/* <button onClick={() => handleEditUser(index)} className="edit-btn"><EditOutlined /></button>  */}
                  <button
                    onClick={() => handleDeleteClick(index, user)}
                    className="delete-btn"
                  >
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" className="no-user-msg">
                No users found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>
              {editingIndex !== null ? "Edit User Details" : "Add New User"}
            </h3>
            <select
              name="selectedStaffId"
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
            >
              <option value="">Select Staff</option>
              {availableStaff.map((staff) => (
                <option key={staff.staff_id} value={staff.staff_id}>
                  {staff.staff_name} - {staff.staff_id}
                </option>
              ))}
            </select>
            <div className="modal-actions">
              <button onClick={handleAddUser} className="add-btn">
                {editingIndex !== null ? "Update" : "Add"}
              </button>
              <button onClick={resetForm} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message="Are you sure you want to delete this user"
        itemName={
          userToDelete?.user?.staff_name || userToDelete?.user?.username
        }
        confirmText="Delete User"
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default Users;
